/**
 * Pre-generated noise tables (built once per context) and the algorithmic reverb impulse.
 * All loops are crossfaded at the seam so they can run forever without a tick.
 */
import { TAU, toBuffer, type Rng } from './util';

export interface NoiseBank {
  /** Stereo, decorrelated channels, RMS ≈ 0.2. */
  readonly white: AudioBuffer;
  readonly pink: AudioBuffer;
  /** Leaky-integrated (corner ≈ 150 Hz). */
  readonly brown: AudioBuffer;
  /** Mono sparse decaying grains: sand, gravel, leaf litter; peak-normalised. */
  readonly crackle: AudioBuffer;
}

export interface ReverbSpec {
  /** RT60-ish decay length (s). */
  readonly seconds: number;
  readonly preDelay: number;
  /** One-pole damping cutoff at the start and end of the tail (Hz) — highs die first. */
  readonly dampStart: number;
  readonly dampEnd: number;
  /** Onset bloom time constant (s): larger = softer, more diffuse attack. */
  readonly bloom: number;
}

const LOOP_SECONDS = 3;
const CRACKLE_SECONDS = 2;
const XFADE = 2048;
const RMS = 0.2;

type Gen = (n: number, rng: Rng) => Float32Array<ArrayBuffer>;

const genWhite: Gen = (n, rng) => {
  const o = new Float32Array(n);
  for (let i = 0; i < n; i++) o[i] = rng.bi();
  return o;
};

/** Paul Kellet's refined pink filter. */
const genPink: Gen = (n, rng) => {
  const o = new Float32Array(n);
  let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
  for (let i = 0; i < n; i++) {
    const w = rng.bi();
    b0 = 0.99886 * b0 + w * 0.0555179;
    b1 = 0.99332 * b1 + w * 0.0750759;
    b2 = 0.969 * b2 + w * 0.153852;
    b3 = 0.8665 * b3 + w * 0.3104856;
    b4 = 0.55 * b4 + w * 0.5329522;
    b5 = -0.7616 * b5 - w * 0.016898;
    o[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
    b6 = w * 0.115926;
  }
  return o;
};

const genBrown: Gen = (n, rng) => {
  const o = new Float32Array(n);
  let last = 0;
  for (let i = 0; i < n; i++) {
    last = (last + 0.02 * rng.bi()) / 1.02;
    o[i] = last;
  }
  removeDc(o);
  return o;
};

function removeDc(d: Float32Array): void {
  let sum = 0;
  for (let i = 0; i < d.length; i++) sum += d[i] as number;
  const mean = sum / Math.max(1, d.length);
  for (let i = 0; i < d.length; i++) d[i] = (d[i] as number) - mean;
}

function normalizeRms(d: Float32Array, target: number): void {
  let acc = 0;
  for (let i = 0; i < d.length; i++) {
    const v = d[i] as number;
    acc += v * v;
  }
  const rms = Math.sqrt(acc / Math.max(1, d.length));
  const k = rms > 1e-9 ? target / rms : 0;
  for (let i = 0; i < d.length; i++) {
    const v = (d[i] as number) * k;
    d[i] = v > 1 ? 1 : v < -1 ? -1 : v;
  }
}

function normalizePeak(d: Float32Array, target: number): void {
  let pk = 0;
  for (let i = 0; i < d.length; i++) pk = Math.max(pk, Math.abs(d[i] as number));
  const k = pk > 1e-9 ? target / pk : 0;
  for (let i = 0; i < d.length; i++) d[i] = (d[i] as number) * k;
}

/** Generate n samples whose end flows seamlessly into their start (equal-power seam crossfade). */
function loopable(gen: Gen, n: number, rng: Rng): Float32Array<ArrayBuffer> {
  const raw = gen(n + XFADE, rng);
  const out = raw.slice(0, n);
  for (let i = 0; i < XFADE; i++) {
    const x = (i / XFADE) * (Math.PI / 2);
    out[i] = (raw[i] as number) * Math.sin(x) + (raw[n + i] as number) * Math.cos(x);
  }
  return out;
}

function stereoLoop(ctx: BaseAudioContext, gen: Gen, rng: Rng): AudioBuffer {
  const n = Math.floor(ctx.sampleRate * LOOP_SECONDS);
  const l = loopable(gen, n, rng);
  const r = loopable(gen, n, rng);
  normalizeRms(l, RMS);
  normalizeRms(r, RMS);
  return toBuffer(ctx, [l, r]);
}

/** Sparse grains with a power-law amplitude spread (lots of tiny ticks, a few bigger pops). */
function genCrackle(ctx: BaseAudioContext, rng: Rng, perSecond: number): AudioBuffer {
  const sr = ctx.sampleRate;
  const n = Math.floor(sr * CRACKLE_SECONDS);
  const o = new Float32Array(n);
  const count = Math.floor(perSecond * CRACKLE_SECONDS);
  for (let k = 0; k < count; k++) {
    const pos = Math.floor(rng.next() * n);
    const amp = Math.pow(rng.next(), 2.2) * (rng.next() < 0.5 ? -1 : 1);
    const len = Math.max(8, Math.floor(sr * rng.range(0.0003, 0.0025)));
    const kd = Math.exp(-5 / len);
    let env = 1;
    for (let j = 0; j < len; j++) {
      const idx = (pos + j) % n; // wraps, so the loop stays seamless
      o[idx] = (o[idx] as number) + amp * env * rng.bi();
      env *= kd;
    }
  }
  normalizePeak(o, 0.95);
  return toBuffer(ctx, [o]);
}

export function createNoiseBank(ctx: BaseAudioContext, rng: Rng): NoiseBank {
  return {
    white: stereoLoop(ctx, genWhite, rng),
    pink: stereoLoop(ctx, genPink, rng),
    brown: stereoLoop(ctx, genBrown, rng),
    crackle: genCrackle(ctx, rng, 280),
  };
}

/**
 * Exponentially-decaying stereo noise impulse with frequency-dependent damping (a darkening
 * one-pole whose cutoff glides from dampStart to dampEnd) and a soft onset bloom.
 */
export function createImpulse(ctx: BaseAudioContext, rng: Rng, spec: ReverbSpec): AudioBuffer {
  const sr = ctx.sampleRate;
  const pre = Math.max(0, Math.floor(spec.preDelay * sr));
  const body = Math.max(64, Math.floor(spec.seconds * sr));
  const len = pre + body;
  const decayK = Math.exp(-6.907755 / (spec.seconds * sr)); // −60 dB at `seconds`
  const ratio = spec.dampEnd / spec.dampStart;
  const bloom = Math.max(0.0005, spec.bloom);
  const chans: Float32Array<ArrayBuffer>[] = [];
  for (let c = 0; c < 2; c++) {
    const d = new Float32Array(len);
    let lp = 0;
    let a = 0;
    let env = 1;
    for (let i = 0; i < body; i++) {
      if ((i & 31) === 0) {
        const fc = spec.dampStart * Math.pow(ratio, i / body);
        a = 1 - Math.exp((-TAU * fc) / sr);
      }
      lp += a * (rng.bi() - lp);
      const onset = 1 - Math.exp(-i / (bloom * sr));
      d[pre + i] = lp * env * onset;
      env *= decayK;
    }
    // Fade the very end so the convolver tail never truncates audibly.
    const tail = Math.min(body, Math.floor(sr * 0.05));
    for (let i = 0; i < tail; i++) {
      const idx = len - 1 - i;
      d[idx] = (d[idx] as number) * (i / tail);
    }
    chans.push(d);
  }
  return toBuffer(ctx, chans);
}
